import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import { ViewProps, DateLocalizer } from 'react-big-calendar';
import { startOfMonth, isSameDay, isSameMonth } from 'date-fns';
import { Box, Grid, useColorMode } from '@chakra-ui/react';

import { DEFAULT_WEEK_DAY, useCalendarSettings } from '../../../../lib';
import { EventList } from './EventList';
import { getMonthRange, getTitle, navigateMonth } from './utils';
import { MonthHeader } from './MonthHeader';
import { DateCells } from './DateCells';

import type { IEvent } from '../../../../lib';

interface IMonthProps extends ViewProps {
	date: Date;
	localizer: DateLocalizer;
	max?: Date;
	min?: Date;
	scrollToTime?: Date;
	events: IEvent[];
}

const Month = ({ date, events }: IMonthProps) => {
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const [selectedEvents, setSelectedEvents] = useState<IEvent[]>([]);
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);

	const currRange = useMemo(() => {
		const startDay = settings?.startOfWeek ?? DEFAULT_WEEK_DAY;
		return getMonthRange(date, startDay);
	}, [date, settings?.startOfWeek]);

	useEffect(() => {
		if (selectedDate) {
			const filteredEvents = events.filter((event) => {
				const eventStart = new Date(event.start);
				const eventEnd = new Date(event.end);
				return (
					isSameDay(eventStart, selectedDate) ||
					isSameDay(eventEnd, selectedDate) ||
					(eventStart <= selectedDate && eventEnd >= selectedDate)
				);
			});
			setSelectedEvents(filteredEvents);
		}
	}, [selectedDate, events]);

	useEffect(() => {
		// Get the first day of the current month
		let selectedCalendarDate = startOfMonth(date);
		// If its current month select currentDate
		if (isSameMonth(date, new Date())) {
			selectedCalendarDate = date;
		}
		setSelectedDate(selectedCalendarDate);
	}, [date]);

	return (
		<Box>
			<Grid
				templateColumns={`repeat(7, 1fr)`}
				templateRows={`repeat(${currRange.length + 1}, auto)`}
				gap={0}
				alignItems='center'
				justifyContent='center'
				border='1px solid'
				borderColor={settings.styles[colorMode].calendarBordersColor}
			>
				<MonthHeader settings={settings} colorMode={colorMode} />

				<DateCells
					currRange={currRange}
					events={events}
					selectedDate={selectedDate}
					setSelectedDate={setSelectedDate}
					settings={settings}
					colorMode={colorMode}
				/>
			</Grid>
			<Box backgroundColor='white' py={4}>
				<EventList events={selectedEvents} />
			</Box>
		</Box>
	);
};

Month.range = getMonthRange;

Month.navigate = navigateMonth;

Month.title = getTitle;

export default Month;
